import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { CouplingSuggestion, SlotBoardRow } from '../../services/pickupSlots.api';
import { addStopToRoute } from '../../services/routes.api';

interface Props {
  row: SlotBoardRow;
  suggestion: CouplingSuggestion;
  onClose: () => void;
}

/**
 * RTO-coupling accept (PRD 6.1.7): appends the pickup's task onto the route of
 * the PA already carrying this merchant's return. Side effects are spelled out:
 * mutates a live route AND edit-locks the merchant's pickup (route_appended).
 */
export default function CoupleSuggestionModal({ row, suggestion, onClose }: Props) {
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => addStopToRoute(suggestion.route_id, suggestion.task_uid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pickupSlotBoard'] });
      queryClient.invalidateQueries({ queryKey: ['pickupSlotCoupling'] });
      onClose();
    },
    onError: (err: { response?: { data?: { error?: string } } }) =>
      setError(err.response?.data?.error ?? 'Failed to append the stop'),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
        <h2 className="text-lg font-semibold text-gray-900">Append to {suggestion.driver_name ?? 'pickup agent'}?</h2>
        <div className="mt-2 space-y-1 text-sm text-gray-600">
          <p>
            <span className="font-medium">{suggestion.driver_name ?? suggestion.driver_uid}</span> is already
            carrying an open return for <span className="font-medium">{row.merchant_name}</span> on route{' '}
            <span className="font-medium">{suggestion.route_name}</span> ({suggestion.route_status}).
          </p>
          <p className="text-amber-700">
            This adds a stop to that live route (zone matching bypassed) and locks the merchant's pickup for
            editing (route_appended).
          </p>
        </div>
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-md border border-gray-300 px-3 py-1.5 text-sm">
            Cancel
          </button>
          <button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {mutation.isPending ? 'Appending…' : 'Append stop'}
          </button>
        </div>
      </div>
    </div>
  );
}
